import { useEffect, useState, type ReactNode } from 'react';
import { Icon } from './Icon';
import { ThemeToggle } from './ThemeToggle';

/**
 * The application shell: header, rail, page.
 *
 * The same structure as the control plane, because it is the same
 * product seen from the other side, and a client who has watched a
 * demo should recognise where things are.
 *
 * Two audiences share it. A supervisor on a laptop gets the rail. A
 * counter PC is a 420px window on shop-floor hardware, where the rail
 * becomes a row of touch targets along the bottom, close to where a
 * thumb already is.
 */
export type NavItem = {
  id: string;
  label: string;
  icon: string;
  count?: number;
  /**
   * Children open beneath the parent rather than inside the page.
   * A section with four panes hides three of them behind a click if
   * its tabs live in the content: in the rail they are all visible,
   * and you can go straight to the one you want.
   */
  children?: Array<{
    id: string;
    label: string;
    count?: number;
    /** Set apart: not another of the same kind of thing. */
    muted?: boolean;
  }>;
};

const RAIL_KEY = 'kr.rail';

export const Shell = ({
  brand, name, deviceName, nav, active, onNavigate,
  title, blurb, headerExtra, navTop, navTitle, version, onAbout, pageTop, fullHeight, children,
}: {
  /** The logo or mark only; the name is rendered beside it. */
  brand: ReactNode;
  /** The product name from branding, not the tenant's legal name. */
  name: string;
  deviceName?: string;
  nav: NavItem[];
  active: string;
  onNavigate: (id: string) => void;
  title: string;
  blurb?: string;
  headerExtra?: ReactNode;
  /** Sits above the menu, for things that scope the whole app. */
  /** What the menu is a list of. Sites, for an assistant. */
  navTitle?: string;
  /** The shell build, when running inside it. */
  version?: string;
  /** Opens the details a support call asks for. */
  onAbout?: () => void;
  navTop?: ReactNode;
  /** The same control for narrow screens, where the nav
      is a bar along the bottom with no room for a select. */
  pageTop?: ReactNode;
  /** One screen that fills the page, with no heading above it. */
  fullHeight?: boolean;
  children: ReactNode;
}) => {
  const host = window.location.hostname;

  const [rail, setRail] = useState(() => {
    try {
      return localStorage.getItem(RAIL_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(RAIL_KEY, rail ? '1' : '0');
    } catch {
      // Private browsing refuses storage; the choice holds for now.
    }
  }, [rail]);

  return (
    <div className={rail ? 'app isRail' : 'app'}>
      <header className="appHead">
        <div className="appBrand">
          {brand}
          {/* Who this is, then where it lives. A supervisor with two
              clients open needs the hostname to tell them apart, and
              a device needs its own name more than either. */}
          <span className="brandText">
            <strong>{name}</strong>
            <span className="brandSub mono">
              {deviceName ?? host}
            </span>
          </span>
        </div>

        <button
          className="iconBtn railToggle"
          onClick={() => setRail(!rail)}
          title={rail ? 'Expand menu' : 'Collapse menu'}
          aria-label={rail ? 'Expand menu' : 'Collapse menu'}
        >
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none"
               stroke="currentColor" strokeWidth="1.8"
               strokeLinecap="round" strokeLinejoin="round">
            <path d={rail ? 'M9 6l6 6-6 6' : 'M15 6l-6 6 6 6'} />
          </svg>
        </button>

        <div className="appHeadRight">
          <ThemeToggle />
          {headerExtra}
        </div>
      </header>

      <nav className="appNav" aria-label="Sections">
        {navTop}
        <p className="navTitle mono">{navTitle ?? 'Menu'}</p>
        <div className="navRow">
        {nav.map((n) => {
          const open = active === n.id
            || (n.children ?? []).some((c) => active === c.id);

          return (
            <div key={n.id} className="navGroup">
              <button
                className={active === n.id || open
                  ? 'navItem on' : 'navItem'}
                onClick={() => onNavigate(
                  n.children?.[0]?.id ?? n.id,
                )}
                title={n.label}
              >
                <Icon name={n.icon} />
                <span className="navLabel">{n.label}</span>
                {(n.count ?? 0) > 0 && <b className="badge">{n.count}</b>}
              </button>

              {/* Children show only for the open section: every
                  submenu expanded at once is a wall, not a menu. */}
              {open && (n.children?.length ?? 0) > 0 && (
                <div className="navKids">
                  {n.children!.map((c) => (
                    <button
                      key={c.id}
                      className={[
                        'navKid',
                        c.muted ? 'navKid--set' : '',
                        active === c.id ? 'on' : '',
                      ].filter(Boolean).join(' ')}
                      onClick={() => onNavigate(c.id)}
                    >
                      <span className="navLabel">{c.label}</span>
                      {(c.count ?? 0) > 0 && (
                        <b className="badge">{c.count}</b>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        </div>
        {/* Whose product this is, and which build. On a counter PC
            this is the only place either is written down, and it is
            the first thing a support call asks for. */}
        <button
          className="navFoot mono"
          onClick={onAbout}
          title="About this screen"
        >
          Powered by Kestrelay
          {version && <span>{version}</span>}
        </button>
      </nav>

      <main className={fullHeight ? "appMain tall" : "appMain"}>
        {pageTop && <div className="pageTop">{pageTop}</div>}
        {!fullHeight && <div className="pageHead">
          <h1>{title}</h1>
          {blurb && <p className="sub">{blurb}</p>}
        </div>}
        {children}
      </main>
    </div>
  );
};
